import React from "react";
import { withTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import * as moment from "moment";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import DatePicker from "react-multi-date-picker";
import AttachedInvoice from "./AttachedInvoice";
import { Dropdown, TextInput, CustomCheckBox } from "hesus-ui-kit";
import UserInfo from "@utils/UserInfo";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  GridRow,
  GridCell,
  SpanButton,
  InvoiceButton,
  FlexRowItem,
  ContainerLabel,
  FlexRow,
} from "../../AddEditInvoice.styled";
import "../../AddEditInvoice.scss";
import { invoiceDocumentTypes, formInvoiceTypes } from "@utils/filterOptions";

const InvoiceDocument = ({
  t,
  isEditMode,
  formData,
  onFormChange,
  setFormData,
  selectedStatus,
  statusOptions,
}) => {
  const params = useParams();
  const countryId = UserInfo.countryId();
  const countryList = UserInfo.countryListForFilter(t);

  const invoiceTypeOptions = formInvoiceTypes(t);

  const getColumnForGrid = () => {
    let columns = 3;
    if (countryList?.length > 1) {
      columns += 1;
    }
    // if (formData.documentType?.value === 1) {
    //   columns += 1;
    // }
    return `repeat(${columns}, 1fr)`;
  };

  return (
    <>
      <GridRow background="#008395" borderRadius="5px 5px 0 0px">
        <GridCell fontWeight="700" fontSize="16px">
          {t("CLIENT_INVOICING_SECTION_DOC_INFO")}
        </GridCell>
      </GridRow>
      <GridRow
        column="75% 1px 23%"
        boxShadow="0px 1px 1px 1px #e5e5e5"
        margin="0 0 12px 0"
        divider
      >
        <GridRow column="1fr" padding="0px">
          <GridRow padding="0px" column={getColumnForGrid()}>
            {countryList?.length > 1 && (
              <Dropdown
                id="invoice-country"
                label={t("COUNTRY")}
                name="country"
                value={formData.countryId.value || ""}
                onChange={(option) => onFormChange("countryId", option.value)}
                defaultOption={t("COUNTRY")}
                options={countryList}
                labelStyles={
                  formData.countryId.error !== ""
                    ? labelStylesError
                    : labelStyles
                }
                inputStyles={
                  formData.countryId.error !== ""
                    ? inputStylesError
                    : inputStyles
                }
                error={formData.countryId.error}
                required
              />
            )}
            <Dropdown
              id="invoice-doc-type"
              required
              label={t("CLIENT_INVOICING_DOCUMENT_TYPE")}
              name="CLIENT_INVOICING_DOCUMENT_TYPE"
              value={formData.documentType.value ?? ""}
              onChange={(option) => onFormChange("documentType", option.value)}
              defaultOption={t("CLIENT_INVOICING_DOCUMENT_TYPE")}
              options={invoiceDocumentTypes(t)}
              labelStyles={
                formData.documentType.error !== ""
                  ? labelStylesError
                  : labelStyles
              }
              inputStyles={
                formData.documentType.error !== ""
                  ? inputStylesError
                  : inputStyles
              }
              error={formData.documentType.error}
            />
            <div
              className={
                formData.startDate.error
                  ? "add-edit-invoice__date-container_start_error"
                  : "add-edit-invoice__date-container_start add-edit-invoice__date-container_start_required"
              }
            >
              <label htmlFor="start-date">
                {t("CLIENT_INVOICING_PERIOD_START")}
              </label>
              <DatePicker
                inputClass={
                  formData.startDate.error
                    ? "add-edit-invoice__date-input-error"
                    : "add-edit-invoice__date-input"
                }
                containerClassName="add-edit-invoice__datepicker-container"
                type="text"
                required
                label={t("CLIENT_INVOICING_PERIOD_START")}
                name="add-edit-invoice-start-date"
                id="add-edit-invoice-start-date"
                calendarPosition="bottom-center"
                isFloat
                maxDate={formData.endDate.value || undefined}
                onChange={(e) =>
                  onFormChange(
                    "startDate",
                    e ? moment(e.toDate()).format("YYYY-MM-DD") : ""
                  )
                }
                format={getDatePickerFormat()}
                locale={getCalendarLocale()}
                value={
                  getDateFormat(formData.startDate.value, "YYYY-MM-DD") || ""
                }
                containerStyles={{ marginTop: "21px" }}
                labelStyles={
                  formData.startDate.error !== ""
                    ? labelStylesError
                    : labelStyles
                }
                error=""
                placeholder={t("CLIENT_INVOICING_PERIOD_START")}
              />
            </div>
            <div
              className={
                formData.endDate.error
                  ? "add-edit-invoice__date-container_start_error"
                  : "add-edit-invoice__date-container_start add-edit-invoice__date-container_start_required"
              }
            >
              <label htmlFor="end-date">
                {t("CLIENT_INVOICING_PERIOD_END")}
              </label>
              <DatePicker
                inputClass={
                  formData.endDate.error
                    ? "add-edit-invoice__date-input-error"
                    : "add-edit-invoice__date-input"
                }
                containerClassName="add-edit-invoice__datepicker-container"
                type="text"
                label={t("CLIENT_INVOICING_PERIOD_END")}
                name="add-edit-invoice-start-date"
                id="add-edit-invoice-start-date"
                calendarPosition="bottom-center"
                minDate={formData.startDate.value || undefined}
                isFloat
                onChange={(e) =>
                  onFormChange(
                    "endDate",
                    e ? moment(e.toDate()).format("YYYY-MM-DD") : ""
                  )
                }
                format={getDatePickerFormat()}
                locale={getCalendarLocale()}
                value={
                  getDateFormat(formData.endDate.value, "YYYY-MM-DD") || ""
                }
                containerStyles={{ marginTop: "21px" }}
                labelStyles={
                  formData.endDate.error !== "" ? labelStylesError : labelStyles
                }
                required
                error=""
                placeholder={t("CLIENT_INVOICING_PERIOD_END")}
              />
            </div>
          </GridRow>
          <GridRow
            column="minmax(250px, 400px) minmax(250px, 400px) minmax(250px, 400px)"
            padding="0px"
          >
            {formData.attachedInvoice.value?.id && (
              <InvoiceButton
                key={"InvoiceButton" + formData.attachedInvoice.value.id}
                border="#004C7F"
                background="#004C7F"
              >
                <>
                  <SpanButton>
                    {formData.attachedInvoice.value?.name}
                  </SpanButton>
                  <SpanButton
                    marginLeft="15px"
                    key={formData.attachedInvoice.value?.id}
                    onClick={() => {
                      setFormData({
                        ...formData,
                        attachedInvoice: {
                          value: [],
                          error: "",
                          required: true,
                        },
                      });
                    }}
                  >
                    <span aria-hidden="true">&times;</span>
                  </SpanButton>
                </>
              </InvoiceButton>
            )}
            {!formData.attachedInvoice.value?.id && (
              <div>
                <AttachedInvoice
                  formData={formData}
                  setFormData={setFormData}
                  isEditMode={isEditMode}
                  countryId={countryId}
                />
              </div>
            )}
            <TextInput
              type="text"
              label={t("CLIENT_INVOICING_ORDER_NUMBER")}
              name="invoice-upsert-order-number"
              id="invoice-upsert-order-number"
              isFloat
              value={String(formData.orderNumber.value)}
              onChange={(e) => onFormChange("orderNumber", e.target.value)}
              containerStyles={{ marginTop: "21px" }}
              labelStyles={labelStyles}
              inputStyles={inputStyles}
              placeholder={t("CLIENT_INVOICING_ORDER_NUMBER")}
            />
            {formData.documentType?.value === 1 && (
              <div>
                <GridRow
                  column="1fr"
                  marginBottom="0px"
                  padding="4px 0px 0px 0px"
                >
                  <FlexRow>
                    <ContainerLabel fontSize="1rem">
                      {t("CLIENT_INVOICING_INVOICE_TYPE")}
                    </ContainerLabel>
                  </FlexRow>
                </GridRow>
                <GridRow
                  column="auto 1fr"
                  padding="0px"
                  alignItems="center"
                  margin="5px 0 0 0"
                >
                  {invoiceTypeOptions.map((type) => (
                    <FlexRowItem key={`${type.value}${"-"}${type.label}`}>
                      <CustomCheckBox
                        id={type.value}
                        labelText={t(type.label)}
                        dataId={type.value}
                        checked={formData.invoiceType.value.includes(
                          type.value
                        )}
                        onChange={(e) =>
                          onFormChange(
                            "invoiceType",
                            { id: type.value, checked: e.target.checked },
                            { isCheckboxGroup: true }
                          )
                        }
                      />
                    </FlexRowItem>
                  ))}
                </GridRow>
              </div>
            )}
          </GridRow>
        </GridRow>
        <div
          style={{
            width: "1px",
            background:
              "linear-gradient(180deg, #FFFFFF 0%, #004C7F 50.28%, #FFFFFF 100.39%)",
          }}
        />
        <GridRow padding="0px">
          <GridRow column="1fr" padding="0px">
            <Dropdown
              id="invoice-CLIENT_INVOICING_STATUS"
              label={t("CLIENT_INVOICING_STATUS")}
              name="CLIENT_INVOICING_STATUS"
              value={formData.statusId.value ?? ""}
              onChange={(option) => onFormChange("statusId", option.value)}
              options={statusOptions}
              labelStyles={labelStyles}
              inputStyles={{
                background: selectedStatus.bgColor || "#fff",
                color: selectedStatus.color || "#000",
              }}
            />
          </GridRow>
          <GridRow
            column="1fr"
            padding="0px"
            alignItems="center"
            margin="0px 0 0 0"
          >
            <FlexRowItem>
              <CustomCheckBox
                key="client-invoice-send-by-email"
                id="client-invoice-send-by-email"
                labelText={t("CLIENT_INVOICING_SEND_BY_EMAIL")}
                dataId="1"
                checked={!!formData.sendByEmail.value}
                onChange={(e) => onFormChange("sendByEmail", e.target.checked)}
              />
            </FlexRowItem>
          </GridRow>
        </GridRow>
      </GridRow>
    </>
  );
};

InvoiceDocument.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  updateRefreshStatus: PropTypes.func,
};

InvoiceDocument.defaultProps = {
  isEditMode: false,
  id: null,
  updateRefreshStatus: null,
};

export default withTranslation()(InvoiceDocument);
